import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  styled,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadIcon from '@mui/icons-material/Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import {
  fetchCashReconLedgerScope,
  appendCashReconLedgerCoaQueryParams,
} from './cashReconLedgerScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDialogContentSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateDataGridSx,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const UploadArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: '8px',
  padding: '30px 20px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.green,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const ChartOfAccountsDrawer = ({ open, onClose }) => {
  const { canAdd, canEdit, canDelete, canUpload } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const { showSuccess, showError, showInfo } = useToast();

  // Accordion state
  const [expanded, setExpanded] = useState('chartOfAccounts');

  // State for Chart of Accounts data
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [chartOfAccountsLoading, setChartOfAccountsLoading] = useState(false);
  const [chartOfAccountsError, setChartOfAccountsError] = useState(null);
  const [selectedChartOfAccountRow, setSelectedChartOfAccountRow] = useState(null);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuChartOfAccount, setRowMenuChartOfAccount] = useState(null);

  // State for filters
  const [propertyGroupFilter, setPropertyGroupFilter] = useState('ALL');
  const [propertyIdFilter, setPropertyIdFilter] = useState('ALL');
  const [accountTypeFilter, setAccountTypeFilter] = useState('ALL');

  // State for property groups
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [propertyGroupsLoading, setPropertyGroupsLoading] = useState(false);

  // State for Add Chart of Account Dialog
  const [addChartOfAccountDialogOpen, setAddChartOfAccountDialogOpen] = useState(false);
  const [newChartOfAccount, setNewChartOfAccount] = useState({
    glAccountCode: '',
    glAccountName: '',
    glAccount: '',
    accountType: 'ASSET',
    accountSubType: 'OTHER',
    propertyId: '',
    groupCode: '',
    cashAccountFlag: false,
    status: 'ACTIVE'
  });
  const [addChartOfAccountLoading, setAddChartOfAccountLoading] = useState(false);
  const [addChartOfAccountError, setAddChartOfAccountError] = useState(null);

  // State for Edit Chart of Account Dialog
  const [editChartOfAccountDialogOpen, setEditChartOfAccountDialogOpen] = useState(false);
  const [editingChartOfAccount, setEditingChartOfAccount] = useState(null);
  const [editChartOfAccountLoading, setEditChartOfAccountLoading] = useState(false);
  const [editChartOfAccountError, setEditChartOfAccountError] = useState(null);

  // State for Delete Chart of Account Dialog
  const [deleteChartOfAccountDialogOpen, setDeleteChartOfAccountDialogOpen] = useState(false);
  const [deletingChartOfAccount, setDeletingChartOfAccount] = useState(null);
  const [deleteChartOfAccountLoading, setDeleteChartOfAccountLoading] = useState(false);
  const [deleteChartOfAccountError, setDeleteChartOfAccountError] = useState(null);

  // State for CSV upload
  const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedPropertyApi, setSelectedPropertyApi] = useState('');
  const [loadingPropertyApis, setLoadingPropertyApis] = useState(false);
  const [propertyApis, setPropertyApis] = useState([]);
  const [selectedAccountSubType, setSelectedAccountSubType] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // State for templates and preview
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    fileName: '',
    templateName: '',
    excelFields: [],
    templateFields: [],
    mappings: {},
    excelData: []
  });
  const [customMappings, setCustomMappings] = useState({});

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setChartOfAccounts([]);
      setChartOfAccountsError(null);
      setPropertyGroups([]);
      setSelectedChartOfAccountRow(null);
      setRowMenuAnchorEl(null);
      setRowMenuChartOfAccount(null);
      setPropertyGroupFilter('ALL');
      setPropertyIdFilter('ALL');
      setAccountTypeFilter('ALL');
      setAddChartOfAccountDialogOpen(false);
      setEditChartOfAccountDialogOpen(false);
      setDeleteChartOfAccountDialogOpen(false);
      setNewChartOfAccount({
        glAccountCode: '',
        glAccountName: '',
        glAccount: '',
        accountType: 'ASSET',
        accountSubType: 'OTHER',
        propertyId: '',
        groupCode: '',
        cashAccountFlag: false,
        status: 'ACTIVE'
      });
      setEditingChartOfAccount(null);
      setDeletingChartOfAccount(null);
      setAddChartOfAccountError(null);
      setEditChartOfAccountError(null);
      setDeleteChartOfAccountError(null);
      setCsvUploadDialogOpen(false);
      setUploadFile(null);
      setUploadProgress(0);
      setUploading(false);
      setUploadError(null);
      setSelectedPropertyApi('');
      setSelectedAccountSubType('');
      setIsDragging(false);
      setSelectedTemplate('');
      setTemplates([]);
      setCustomMappings({});
      setPreviewOpen(false);
      setPreviewData({
        fileName: '',
        templateName: '',
        excelFields: [],
        templateFields: [],
        mappings: {},
        excelData: []
      });
      setExpanded('chartOfAccounts');
    }
  }, [open]);

  // Fetch Chart of Accounts data
  const fetchChartOfAccounts = async () => {
    setChartOfAccountsLoading(true);
    setChartOfAccountsError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Build URL with filter parameters
      let url = `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}`;

      // Add account type filter if not ALL
      if (accountTypeFilter !== 'ALL') {
        url += `&account_type=${accountTypeFilter}`;
      }

      // Add property ID filter if not ALL
      if (propertyIdFilter !== 'ALL') {
        url += `&property_id=${propertyIdFilter}`;
      }

      url = await appendCashReconLedgerCoaQueryParams(url, { baseUrl, tenantId, token });

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch chart of accounts: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && Array.isArray(data)) {
        // Map API response to grid format
        const processedData = data.map((item, index) => ({
          id: item.ledgerAccountId || index + 1,
          ledgerAccountId: item.ledgerAccountId,
          glAccountCode: item.glCode,
          glAccountName: item.glDescription,
          glAccount: item.glAccount,
          accountType: item.accountType,
          accountSubType: item.accountSubType,
          propertyId: item.propertyId,
          groupCode: item.groupCode,
          cashAccountFlag: item.isCashAccount,
          isCashAccount: item.isCashAccount,
          currency: item.currency,
          status: item.status,
          tenantId: item.tenantId,
          glSystem: item.glSystem,
          parentAccountId: item.parentAccountId,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }));
        setChartOfAccounts(processedData);
      } else {
        setChartOfAccounts([]);
      }
    } catch (err) {
      console.error('Error fetching chart of accounts:', err);
      setChartOfAccountsError(err.message || 'Failed to load chart of accounts');
      setChartOfAccounts([]);
    } finally {
      setChartOfAccountsLoading(false);
    }
  };

  // Fetch chart of accounts when drawer opens or filters change
  useEffect(() => {
    if (open) {
      fetchChartOfAccounts();
      fetchPropertyApis();
      fetchPropertyGroups();
    }
  }, [open, propertyGroupFilter, propertyIdFilter, accountTypeFilter]);

  // Fetch Properties (using correct API endpoint)
  const fetchPropertyApis = async () => {
    setLoadingPropertyApis(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Use the correct API endpoint
      const url = `${baseUrl}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && Array.isArray(data)) {
        const processedData = data.map((item, index) => ({
          id: item.propertyId || index + 1,
          propertyId: item.propertyId || '',
          propertyName: item.propertyName || '',
          propertyCode: item.propertyCode || '',
          propertyGroupId: item.propertyGroupId || '',
          companyId: item.companyId || '',
          addressLine1: item.addressLine1 || '',
          city: item.city || '',
          state: item.state || '',
          country: item.country || '',
          postalCode: item.postalCode || '',
          baseCurrency: item.baseCurrency || 'USD',
          defaultBankAccountId: item.defaultBankAccountId || null,
          cashReconEnabled: item.cashReconEnabled !== false,
          status: item.status || 'ACTIVE',
          deleted: item.deleted || false,
          createdAt: item.createdAt || '',
          updatedAt: item.updatedAt || '',
        }));
        setPropertyApis(processedData);
      } else {
        setPropertyApis([]);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setPropertyApis([]);
    } finally {
      setLoadingPropertyApis(false);
    }
  };

  // Fetch Property Groups
  const fetchPropertyGroups = async () => {
    setPropertyGroupsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/property-groups/by-tenant?tenant_id=${tenantId}`;

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch property groups: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (data && Array.isArray(data)) {
        setPropertyGroups(data);
      } else {
        setPropertyGroups([]);
      }
    } catch (err) {
      console.error('Error fetching property groups:', err);
      setPropertyGroups([]);
    } finally {
      setPropertyGroupsLoading(false);
    }
  };

  // Fetch templates for Chart of Accounts
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // For Chart of Accounts, we'll use a specific endpoint or filter
      const url = `${baseUrl}conductor/ref-templates/by-document-type?documentTypeId=CHART_OF_ACCOUNTS&tenant_id=${tenantId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        // If the endpoint doesn't exist, create default templates
        if (resp.status === 404) {
          const defaultTemplates = [
            {
              id: 'chart_of_accounts_default',
              name: 'Chart of Accounts Default',
              description: 'Default template for Chart of Accounts upload',
              mappings: {
                'GL_CODE': { targetColumn: 'glCode', dataType: 'TEXT', isRequired: true },
                'GL_DESCRIPTION': { targetColumn: 'glDescription', dataType: 'TEXT', isRequired: true },
                'GL_ACCOUNT': { targetColumn: 'glAccount', dataType: 'TEXT', isRequired: false },
                'ACCOUNT_TYPE': { targetColumn: 'accountType', dataType: 'TEXT', isRequired: false },
                'ACCOUNT_SUB_TYPE': { targetColumn: 'accountSubType', dataType: 'TEXT', isRequired: false }
              }
            }
          ];
          setTemplates(defaultTemplates);
        } else {
          throw new Error(`Failed to fetch templates: ${resp.status} ${resp.statusText}`);
        }
      } else {
        const data = await resp.json();
        const transformedTemplates = Array.isArray(data) ? data.map(template => ({
          id: template.templateId,
          name: template.templateName,
          description: template.description,
          mappings: template.mapping || {}
        })) : [];
        setTemplates(transformedTemplates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      // Create default templates on error
      const defaultTemplates = [
        {
          id: 'chart_of_accounts_default',
          name: 'Chart of Accounts Default',
          description: 'Default template for Chart of Accounts upload',
          mappings: {
            'GL_CODE': { targetColumn: 'glCode', dataType: 'TEXT', isRequired: true },
            'GL_DESCRIPTION': { targetColumn: 'glDescription', dataType: 'TEXT', isRequired: true },
            'GL_ACCOUNT': { targetColumn: 'glAccount', dataType: 'TEXT', isRequired: false },
            'ACCOUNT_TYPE': { targetColumn: 'accountType', dataType: 'TEXT', isRequired: false },
            'ACCOUNT_SUB_TYPE': { targetColumn: 'accountSubType', dataType: 'TEXT', isRequired: false }
          }
        }
      ];
      setTemplates(defaultTemplates);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Fetch templates when dialog opens
  useEffect(() => {
    if (csvUploadDialogOpen) {
      fetchTemplates();
    }
  }, [csvUploadDialogOpen]);

  // Function to dynamically load XLSX library
  const loadXLSX = async () => {
    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
      return XLSX;
    } catch (error) {
      console.warn('Failed to load XLSX from CDN, trying local import:', error);
      const XLSX = await import('xlsx');
      return XLSX;
    }
  };

  // Convert value based on data type
  const convertValue = (value, dataType) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const strValue = String(value).trim();
    const type = (dataType || 'TEXT').toUpperCase();

    switch (type) {
      case 'NUMBER':
      case 'INTEGER':
      case 'FLOAT':
      case 'DECIMAL':
        const numValue = strValue.replace(/[^0-9.-]/g, '');
        return numValue === '' ? null : Number(numValue);
      case 'BOOLEAN':
        const lowerValue = strValue.toLowerCase();
        if (['true', 'yes', 'y', '1', 't'].includes(lowerValue)) return true;
        if (['false', 'no', 'n', '0', 'f'].includes(lowerValue)) return false;
        return Boolean(strValue);
      case 'DATE':
        const date = new Date(strValue);
        return isNaN(date.getTime()) ? strValue : date.toISOString().split('T')[0];
      case 'TEXT':
      default:
        return strValue.replace(/\s+/g, ' ').trim();
    }
  };

  // Extract fields and data from Excel file
  const extractFieldsFromSheet = async (file) => {
    try {
      const XLSX = await loadXLSX();
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      if (!jsonData || jsonData.length === 0) {
        return { fields: [], data: [] };
      }

      const headers = jsonData[0].map(h => h ? h.toString().trim() : '').filter(h => h !== '');
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));

      const dataRows = rows.map(row => {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = row[index] || '';
        });
        return obj;
      });

      return { fields: headers, data: dataRows };
    } catch (error) {
      console.error('Error extracting fields from sheet:', error);
      return { fields: [], data: [] };
    }
  };

  // Handle preview button click
  const handlePreviewClick = async () => {
    if (!selectedTemplate || !uploadFile) return;

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      if (!template) {
        throw new Error('Selected template not found');
      }

      const { fields: excelFields, data: excelData } = await extractFieldsFromSheet(uploadFile);

      // Build field mappings
      const fieldMappings = {};
      const templateFields = [];

      if (template.mappings) {
        Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
          if (mapping && mapping.isActive !== false) {
            const matchedField = excelFields.find(field =>
              field.toLowerCase() === sourceField.toLowerCase()
            );

            if (matchedField) {
              fieldMappings[sourceField] = {
                targetColumn: mapping.targetColumn || sourceField,
                dataType: mapping.dataType || 'TEXT',
                isRequired: mapping.isRequired || false,
                excelField: matchedField
              };
              templateFields.push(sourceField);
            }
          }
        });
      }

      // Apply custom mappings
      Object.entries(customMappings).forEach(([excelField, templateField]) => {
        if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
          const mapping = template.mappings?.[templateField];
          fieldMappings[templateField] = {
            targetColumn: mapping?.targetColumn || templateField,
            dataType: mapping?.dataType || 'TEXT',
            isRequired: mapping?.isRequired || false,
            excelField: excelField
          };
          if (!templateFields.includes(templateField)) {
            templateFields.push(templateField);
          }
        }
      });

      // Process excel data
      const processedData = excelData.map(row => {
        const mappedRow = {};
        templateFields.forEach(field => {
          const mapping = fieldMappings[field];
          if (mapping?.excelField && row[mapping.excelField] !== undefined) {
            mappedRow[field] = convertValue(row[mapping.excelField], mapping.dataType);
          }
        });
        return mappedRow;
      });

      setPreviewData({
        fileName: uploadFile.name,
        templateName: template.name || 'Unnamed Template',
        excelFields: excelFields,
        templateFields: templateFields,
        mappings: fieldMappings,
        excelData: processedData
      });

      setPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setUploadError(`Failed to generate preview: ${error.message}`);
    }
  };

  // Handle custom field mapping
  const handleCustomMapping = (excelField, templateField) => {
    setCustomMappings(prev => {
      const newMappings = { ...prev };

      // Remove any previous mapping for this excel field
      Object.keys(newMappings).forEach(key => {
        if (newMappings[key] === templateField) {
          delete newMappings[key];
        }
      });

      // Add new mapping if templateField is provided
      if (templateField && templateField !== '') {
        newMappings[excelField] = templateField;
      } else {
        delete newMappings[excelField];
      }

      return newMappings;
    });
  };

  // Handle preview apply
  const handlePreviewApply = () => {
    setPreviewOpen(false);
    showSuccess('Field mappings applied successfully');
  };

  // Process upload helper function
  const processUpload = async (accountsData, url, token) => {
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountsData),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to upload chart of accounts: ${resp.status} ${resp.statusText} - ${errorText}`);
      }

      const result = await resp.json();

      if (result.success) {
        await fetchChartOfAccounts();

        const selectedProperty = propertyApis.find(p => p.propertyId === selectedPropertyApi);
        const propertyName = selectedProperty ? selectedProperty.propertyName : selectedPropertyApi;
        showSuccess(`Successfully uploaded ${result.data?.length || accountsData.length} Chart of Accounts for ${propertyName}`);
        setCsvUploadDialogOpen(false);
        setUploadFile(null);
        setSelectedPropertyApi('');
        setSelectedTemplate('');
        setCustomMappings({});
        setUploadProgress(0);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (apiError) {
      console.error('API Error:', apiError);
      setUploadError(apiError.message || 'Failed to upload chart of accounts');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // CSV Upload Functions
  const handleOpenCsvUploadDialog = (e) => {
    if (!canUpload) return;
    e.stopPropagation();
    setCsvUploadDialogOpen(true);
    setUploadError(null);
    setUploadFile(null);
  };

  // Handle template selection change
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    setCustomMappings({}); // Reset custom mappings when template changes
  };

  // Get available template fields for mapping
  const getAvailableTemplateFields = (currentExcelField, currentMappedTemplateField) => {
    if (!selectedTemplate) return [];

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return [];

    const allTemplateFields = Object.keys(template.mappings || {})
      .filter(field => {
        const mapping = template.mappings[field];
        return mapping && mapping.isActive !== false;
      });

    // Get currently used template fields from mappings
    const usedFields = new Set();
    Object.entries(customMappings).forEach(([excelField, templateField]) => {
      if (excelField !== currentExcelField) {
        usedFields.add(templateField);
      }
    });

    // Return all fields that are either unused or currently selected
    return allTemplateFields
      .filter(field => !usedFields.has(field) || field === currentMappedTemplateField)
      .sort((a, b) => {
        // Sort required fields first, then alphabetically
        const aRequired = template.mappings[a]?.isRequired ? 0 : 1;
        const bRequired = template.mappings[b]?.isRequired ? 0 : 1;

        if (aRequired !== bRequired) {
          return aRequired - bRequired;
        }

        return a.localeCompare(b);
      });
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        setUploadFile(file);
        setUploadError(null);
      } else {
        setUploadError('Please upload a CSV or Excel file');
      }
    }
  };

  // Handle Add Chart of Account Dialog
  const handleOpenAddChartOfAccountDialog = (e) => {
    if (!canAdd) return;
    e.stopPropagation();
    setAddChartOfAccountDialogOpen(true);
    setAddChartOfAccountError(null);
    setNewChartOfAccount({
      glAccountCode: '',
      glAccountName: '',
      glAccount: '',
      accountType: 'ASSET',
      accountSubType: 'OTHER',
      propertyId: '',
      groupCode: '',
      cashAccountFlag: false,
      status: 'ACTIVE'
    });
  };

  // Handle Edit Chart of Account Dialog
  const handleOpenEditChartOfAccountDialog = (e, chartOfAccount = selectedChartOfAccountRow) => {
    if (!canEdit) return;
    e?.stopPropagation();
    if (chartOfAccount) {
      setEditingChartOfAccount({ ...chartOfAccount });
      setEditChartOfAccountDialogOpen(true);
      setEditChartOfAccountError(null);
    }
  };

  // Handle Delete Chart of Account Dialog
  const handleOpenDeleteChartOfAccountDialog = (e, chartOfAccount = selectedChartOfAccountRow) => {
    if (!canDelete) return;
    e?.stopPropagation();
    if (chartOfAccount) {
      setDeletingChartOfAccount({ ...chartOfAccount });
      setDeleteChartOfAccountDialogOpen(true);
      setDeleteChartOfAccountError(null);
    }
  };

  // Handle Add Chart of Account
  const handleAddChartOfAccount = async () => {
    const errors = [];
    if (!newChartOfAccount.propertyId) {
      errors.push('Property');
    }
    if (!newChartOfAccount.glAccountCode.trim()) {
      errors.push('GL Code');
    }
    if (!newChartOfAccount.glAccountName.trim()) {
      errors.push('GL Description');
    }

    if (errors.length > 0) {
      setAddChartOfAccountError(`Required fields are missing: ${errors.join(', ')}`);
      return;
    }

    setAddChartOfAccountLoading(true);
    setAddChartOfAccountError(null);

    try {
      // Validation for required fields
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/chart-of-account`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      const { systemType, glHeaderId } = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });

      const accountData = {
        tenantId: tenantId,
        glCode: newChartOfAccount.glAccountCode,
        glDescription: newChartOfAccount.glAccountName,
        glAccount: newChartOfAccount.glAccount || `${newChartOfAccount.glAccountCode} - ${newChartOfAccount.glAccountName}`,
        glSystem: 'GENERAL_LEDGER',
        systemType,
        glHeaderId,
        accountType: newChartOfAccount.accountType,
        accountSubType: newChartOfAccount.accountSubType,
        isCashAccount: newChartOfAccount.cashAccountFlag,
        groupCode: newChartOfAccount.groupCode,
        propertyId: newChartOfAccount.propertyId,
        parentAccountId: null,
        currency: 'USD',
        status: newChartOfAccount.status
      };

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountData),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to add chart of account: ${resp.status} ${resp.statusText} - ${errorText}`);
      }

      const result = await resp.json();

      // Handle different response formats
      // API might return data directly or wrapped in success property
      if (result.success || (result && result.ledgerAccountId)) {
        await fetchChartOfAccounts();
        showSuccess('Chart of Account added successfully');
        setAddChartOfAccountDialogOpen(false);
        setNewChartOfAccount({
          glAccountCode: '',
          glAccountName: '',
          glAccount: '',
          accountType: '',
          accountSubType: '',
          propertyId: '',
          groupCode: '',
          cashAccountFlag: false,
          status: 'ACTIVE'
        });
      } else {
        throw new Error(result.message || 'Failed to add chart of account');
      }
    } catch (err) {
      console.error('Error adding chart of account:', err);
      setAddChartOfAccountError(err.message || 'Failed to add chart of account');
    } finally {
      setAddChartOfAccountLoading(false);
    }
  };

  // Handle Edit Chart of Account
  const handleEditChartOfAccount = async () => {
    setEditChartOfAccountLoading(true);
    setEditChartOfAccountError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/chart-of-account/${editingChartOfAccount.ledgerAccountId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      const { systemType, glHeaderId } = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });

      const accountData = {
        tenantId: tenantId,
        glCode: editingChartOfAccount.glAccountCode,
        glDescription: editingChartOfAccount.glAccountName,
        glAccount: editingChartOfAccount.glAccount || `${editingChartOfAccount.glAccountCode} - ${editingChartOfAccount.glAccountName}`,
        glSystem: editingChartOfAccount.glSystem || 'GENERAL_LEDGER',
        systemType,
        glHeaderId,
        accountType: editingChartOfAccount.accountType || 'ASSET',
        accountSubType: editingChartOfAccount.accountSubType || 'OTHER',
        isCashAccount: editingChartOfAccount.cashAccountFlag || false,
        groupCode: editingChartOfAccount.groupCode || '',
        propertyId: editingChartOfAccount.propertyId,
        parentAccountId: editingChartOfAccount.parentAccountId || null,
        currency: editingChartOfAccount.currency || 'USD',
        status: editingChartOfAccount.status || 'ACTIVE'
      };

      const resp = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountData),
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to update chart of account: ${resp.status} ${resp.statusText} - ${errorText}`);
      }

      const result = await resp.json();

      // Handle different response formats
      // API might return data directly or wrapped in success property
      if (result.success || (result && result.ledgerAccountId)) {
        await fetchChartOfAccounts();
        showSuccess('Chart of Account updated successfully');
        setEditChartOfAccountDialogOpen(false);
        setEditingChartOfAccount(null);
      } else {
        throw new Error(result.message || 'Failed to update chart of account');
      }
    } catch (err) {
      console.error('Error updating chart of account:', err);
      setEditChartOfAccountError(err.message || 'Failed to update chart of account');
    } finally {
      setEditChartOfAccountLoading(false);
    }
  };

  // Handle Delete Chart of Account
  const handleDeleteChartOfAccount = async () => {
    setDeleteChartOfAccountLoading(true);
    setDeleteChartOfAccountError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/chart-of-account/${deletingChartOfAccount.ledgerAccountId}`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to delete chart of account: ${resp.status} ${resp.statusText} - ${errorText}`);
      }

      // Handle 204 No Content response (successful deletion)
      if (resp.status === 204) {
        await fetchChartOfAccounts();
        showSuccess('Chart of Account deleted successfully');
        setDeleteChartOfAccountDialogOpen(false);
        setDeletingChartOfAccount(null);
        setSelectedChartOfAccountRow(null);
        return;
      }

      const result = await resp.json();

      if (result.success) {
        await fetchChartOfAccounts();
        showSuccess('Chart of Account deleted successfully');
        setDeleteChartOfAccountDialogOpen(false);
        setDeletingChartOfAccount(null);
        setSelectedChartOfAccountRow(null);
      } else {
        throw new Error(result.message || 'Failed to delete chart of account');
      }
    } catch (err) {
      console.error('Error deleting chart of account:', err);
      setDeleteChartOfAccountError(err.message || 'Failed to delete chart of account');
    } finally {
      setDeleteChartOfAccountLoading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!selectedPropertyApi || !uploadFile) {
      setUploadError('Please select a Property API and upload a file.');
      return;
    }

    if (!selectedTemplate) {
      setUploadError('Please select a template before uploading.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const url = `${baseUrl}conductor/cash-recon/chart-of-account/bulk`;

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      let accountsData = [];

      // If template is selected, use template-based processing
      if (selectedTemplate) {
        const template = templates.find(t => t.id === selectedTemplate);
        if (template) {
          const { fields: excelFields, data: excelData } = await extractFieldsFromSheet(uploadFile);

          // Build field mappings including custom mappings
          const fieldMappings = {};
          const templateFields = [];

          if (template.mappings) {
            Object.entries(template.mappings).forEach(([sourceField, mapping]) => {
              if (mapping && mapping.isActive !== false) {
                const matchedField = excelFields.find(field =>
                  field.toLowerCase() === sourceField.toLowerCase()
                );

                if (matchedField) {
                  fieldMappings[sourceField] = {
                    targetColumn: mapping.targetColumn || sourceField,
                    dataType: mapping.dataType || 'TEXT',
                    isRequired: mapping.isRequired || false,
                    excelField: matchedField
                  };
                  templateFields.push(sourceField);
                }
              }
            });
          }

          // Add custom mappings
          Object.entries(customMappings).forEach(([excelField, templateField]) => {
            if (!fieldMappings[templateField] && excelFields.includes(excelField)) {
              const mapping = template.mappings?.[templateField];
              fieldMappings[templateField] = {
                targetColumn: mapping?.targetColumn || templateField,
                dataType: mapping?.dataType || 'TEXT',
                isRequired: mapping?.isRequired || false,
                excelField: excelField
              };
              if (!templateFields.includes(templateField)) {
                templateFields.push(templateField);
              }
            }
          });

          // Process data using mappings
          accountsData = excelData.map(row => {
            const mappedRow = {
              tenantId: tenantId,
              glCode: '',
              glDescription: '',
              glAccount: '',
              glSystem: 'GENERAL_LEDGER',
              accountType: 'ASSET',
              accountSubType: selectedAccountSubType || 'OTHER',
              isCashAccount: selectedAccountSubType?.toLowerCase().includes('cash') || false,
              groupCode: '',
              propertyId: selectedPropertyApi,
              parentAccountId: null,
              currency: 'USD',
              status: 'ACTIVE'
            };

            templateFields.forEach(field => {
              const mapping = fieldMappings[field];
              if (mapping?.excelField && row[mapping.excelField] !== undefined) {
                const value = convertValue(row[mapping.excelField], mapping.dataType);

                // Map to the correct fields based on target column
                switch (mapping.targetColumn) {
                  case 'glCode':
                    mappedRow.glCode = value || '';
                    break;
                  case 'glDescription':
                    mappedRow.glDescription = value || '';
                    break;
                  case 'glAccount':
                    mappedRow.glAccount = value || '';
                    break;
                  case 'accountType':
                    mappedRow.accountType = value || 'ASSET';
                    break;
                  case 'accountSubType':
                    mappedRow.accountSubType = value || selectedAccountSubType || 'OTHER';
                    break;
                }
              }
            });

            // Generate group code if not provided
            if (!mappedRow.groupCode && mappedRow.glCode) {
              mappedRow.groupCode = mappedRow.glCode.substring(0, 4) + '0';
            }

            // Generate glAccount if not provided
            if (!mappedRow.glAccount && mappedRow.glCode && mappedRow.glDescription) {
              mappedRow.glAccount = `${mappedRow.glCode} - ${mappedRow.glDescription}`;
            }

            return mappedRow;
          }).filter(account => account.glCode && account.glDescription);
        }
      } else {
        // Original CSV processing logic
        const reader = new FileReader();

        reader.onload = async (e) => {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length === 0) {
            setUploadError('File is empty');
            setUploading(false);
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

          const glCodeIndex = headers.findIndex(h => h.toLowerCase().includes('gl_code'));
          const glDescriptionIndex = headers.findIndex(h => h.toLowerCase().includes('gl_description'));
          const glAccountIndex = headers.findIndex(h => h.toLowerCase().includes('gl_account'));
          const accountTypeIndex = headers.findIndex(h => h.toLowerCase().includes('account_type'));
          const accountSubTypeIndex = headers.findIndex(h => h.toLowerCase().includes('account_sub_type'));

          if (glCodeIndex === -1 || glDescriptionIndex === -1) {
            setUploadError('Required headers not found. Please ensure your file has GL_CODE and GL_DESCRIPTION headers.');
            setUploading(false);
            return;
          }

          accountsData = lines.slice(1).map((line) => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            if (values[glCodeIndex] && values[glDescriptionIndex]) {
              return {
                tenantId: tenantId,
                glCode: values[glCodeIndex],
                glDescription: values[glDescriptionIndex],
                glAccount: values[glAccountIndex] || `${values[glCodeIndex]} - ${values[glDescriptionIndex]}`,
                glSystem: 'GENERAL_LEDGER',
                accountType: values[accountTypeIndex] || 'ASSET',
                accountSubType: selectedAccountSubType || 'OTHER',
                isCashAccount: selectedAccountSubType?.toLowerCase().includes('cash') || false,
                groupCode: values[glCodeIndex].substring(0, 4) + '0',
                propertyId: selectedPropertyApi,
                parentAccountId: null,
                currency: 'USD',
                status: 'ACTIVE'
              };
            }
            return null;
          }).filter(account => account !== null);

          if (accountsData.length === 0) {
            setUploadError('No valid accounts found in the file.');
            setUploading(false);
            return;
          }

          await processUpload(accountsData, url, token);
        };

        reader.onerror = () => {
          setUploadError('Failed to read the file');
          setUploading(false);
          setUploadProgress(0);
        };

        reader.readAsText(uploadFile);
        return;
      }

      if (accountsData.length === 0) {
        setUploadError('No valid accounts found in the file.');
        setUploading(false);
        return;
      }

      await processUpload(accountsData, url, token);

    } catch (err) {
      console.error('Error uploading CSV:', err);
      setUploadError(err.message || 'Failed to upload CSV');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Helper function to get property name by ID
  const getPropertyNameById = (propertyId) => {
    if (!propertyId) return 'N/A';
    const property = propertyApis.find(p => p.propertyId === propertyId);
    return property ? property.propertyName : propertyId;
  };

  // Columns for Chart of Accounts Grid
  const chartOfAccountsColumns = [
    {
      field: 'glAccountCode',
      headerName: 'GL Account Code',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'glAccountName',
      headerName: 'GL Account Name',
      flex: 2,
      minWidth: 180,
    },
    {
      field: 'accountType',
      headerName: 'Account Type',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'propertyId',
      headerName: 'Property Name',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => getPropertyNameById(params.value),
    },
    {
      field: 'groupCode',
      headerName: 'Group Code',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'cashAccountFlag',
      headerName: 'Cash Account Flag',
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => params.value ? 'Yes' : 'No',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 80,
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label={`Actions for ${params.row.glAccountName}`}
          onClick={(event) => {
            event.stopPropagation();
            setRowMenuAnchorEl(event.currentTarget);
            setRowMenuChartOfAccount(params.row);
          }}
          sx={harmonateGridActionIconSx}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
          bgcolor: ht.drawerBg,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Typography variant="h6" sx={{ flex: 1, color: ht.white, fontWeight: 700 }}>
          Chart of Accounts Management
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={harmonateDrawerBodySx}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        {/* Chart of Accounts Accordion */}
        <Accordion
          expanded={expanded === 'chartOfAccounts'}
          onChange={() => setExpanded(expanded === 'chartOfAccounts' ? false : 'chartOfAccounts')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Chart of Accounts</Typography>
            <IconButton
              onClick={handleOpenEditChartOfAccountDialog}
              size="small"
              disabled={!selectedChartOfAccountRow || !canEdit}
              sx={harmonateDrawerEditIconSx}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenDeleteChartOfAccountDialog}
              size="small"
              disabled={!selectedChartOfAccountRow || !canDelete}
              sx={harmonateDrawerDeleteIconSx}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleOpenAddChartOfAccountDialog}
              size="small"
              disabled={!canAdd}
              sx={harmonateDrawerAddIconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            {/* <IconButton
              onClick={handleOpenCsvUploadDialog}
              size="small"
              color="primary"
            >
              <UploadIcon fontSize="small" />
            </IconButton> */}
          </AccordionSummary>
          <AccordionDetails sx={{
            ...harmonateDrawerAccordionDetailsSx,
            height: 400,
            overflow: 'hidden',
            p: 2,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Filters */}
            <Box sx={{ mb: 2, ...harmonateFormFieldsSx }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {/* <Autocomplete
                  fullWidth
                  size="small"
                  disabled={chartOfAccountsLoading}
                  options={[{ propertyId: 'ALL', propertyName: 'All Properties' }, ...propertyApis]}
                  getOptionLabel={(option) => {
                    if (option.propertyId === 'ALL') {
                      return option.propertyName;
                    }
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find(p => p.propertyId === propertyIdFilter) || { propertyId: 'ALL', propertyName: 'All Properties' }}
                  onChange={(event, newValue) => {
                    setPropertyIdFilter(newValue ? newValue.propertyId : 'ALL');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Property"
                      placeholder="Select Property"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {chartOfAccountsLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                  sx={{ fontSize: '14px' }}
                /> */}

                <FormControl
                  size="small"
                  sx={{ width: { xs: '100%', sm: '50%' } }}
                >
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={accountTypeFilter}
                    label="Account Type"
                    onChange={(e) => setAccountTypeFilter(e.target.value)}
                    disabled={chartOfAccountsLoading}
                  >
                    <MenuItem value="ALL">All Types</MenuItem>
                    <MenuItem value="ASSET">ASSET</MenuItem>
                    <MenuItem value="LIABILITY">LIABILITY</MenuItem>
                    <MenuItem value="EQUITY">EQUITY</MenuItem>
                    <MenuItem value="INCOME">INCOME</MenuItem>
                    <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {chartOfAccountsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress />
              </Box>
            ) : chartOfAccountsError ? (
              <Alert severity="error">{chartOfAccountsError}</Alert>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 350 }}>
                <DataGridPro
                  rows={chartOfAccounts}
                  columns={chartOfAccountsColumns}
                  columnResizeMode="onEnd"
                  resizable
                  sx={{ ...harmonateDataGridSx, border: 'none', flex: 1 }}
                  getRowId={(row) => row.id}
                  rowHeight={35}
                  onRowSelectionModelChange={(newSelection) => {
                    if (newSelection.length > 0) {
                      const selectedRow = chartOfAccounts.find(row => row.id === newSelection[0]);
                      setSelectedChartOfAccountRow(selectedRow);
                    } else {
                      setSelectedChartOfAccountRow(null);
                    }
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography>No chart of accounts found.</Typography>
                      </Box>
                    )
                  }}
                  pagination
                  pageSize={100}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={() => {
          setRowMenuAnchorEl(null);
          setRowMenuChartOfAccount(null);
        }}
        PaperProps={{ sx: { borderRadius: ht.fieldRadius } }}
      >
        <MenuItem onClick={() => {
          handleOpenEditChartOfAccountDialog(null, rowMenuChartOfAccount);
          setRowMenuAnchorEl(null);
          setRowMenuChartOfAccount(null);
        }}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          handleOpenDeleteChartOfAccountDialog(null, rowMenuChartOfAccount);
          setRowMenuAnchorEl(null);
          setRowMenuChartOfAccount(null);
        }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Add Chart of Account Dialog */}
      <Dialog
        open={addChartOfAccountDialogOpen}
        onClose={() => !addChartOfAccountLoading && setAddChartOfAccountDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Add New Chart of Account
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                fullWidth
                size='small'
                options={propertyApis}
                getOptionLabel={(option) => {
                  const code = option.propertyCode || '';
                  const name = option.propertyName || '';
                  return code && name ? `${code} - ${name}` : name;
                }}
                value={propertyApis.find(p => p.propertyId === newChartOfAccount.propertyId) || null}
                onChange={(event, newValue) => {
                  setNewChartOfAccount({ ...newChartOfAccount, propertyId: newValue ? newValue.propertyId : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property"
                    required
                    size='small'
                    placeholder="Select Property"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addChartOfAccountLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      )
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                sx={{ fontSize: '14px' }}
              />
              <TextField
                label="GL Code"
                fullWidth
                size='small'
                required
                value={newChartOfAccount.glAccountCode}
                onChange={(e) => {
                  const newCode = e.target.value;
                  setNewChartOfAccount({
                    ...newChartOfAccount,
                    glAccountCode: newCode,
                    glAccount: newCode && newChartOfAccount.glAccountName ? `${newCode} - ${newChartOfAccount.glAccountName}` : ''
                  });
                }}
                disabled={addChartOfAccountLoading}
              />
            </Box>
            <TextField
              label="GL Description"
              fullWidth
              size='small'
              required
              value={newChartOfAccount.glAccountName}
              onChange={(e) => {
                const newDescription = e.target.value;
                setNewChartOfAccount({
                  ...newChartOfAccount,
                  glAccountName: newDescription,
                  glAccount: newChartOfAccount.glAccountCode && newDescription ? `${newChartOfAccount.glAccountCode} - ${newDescription}` : ''
                });
              }}
              disabled={addChartOfAccountLoading}
            />
            <TextField
              label="GL Account"
              fullWidth
              size='small'
              value={newChartOfAccount.glAccount}
              disabled={true}
            // helperText="Auto-populated from GL Code and GL Description"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={newChartOfAccount.accountType}
                  size='small'
                  label="Account Type"
                  onChange={(e) => setNewChartOfAccount({ ...newChartOfAccount, accountType: e.target.value })}
                  disabled={addChartOfAccountLoading}
                >
                  <MenuItem value="ASSET">ASSET</MenuItem>
                  <MenuItem value="LIABILITY">LIABILITY</MenuItem>
                  <MenuItem value="EQUITY">EQUITY</MenuItem>
                  <MenuItem value="INCOME">INCOME</MenuItem>
                  <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Account Sub Type</InputLabel>
                <Select
                  value={newChartOfAccount.accountSubType || ''}
                  size='small'
                  label="Account Sub Type"
                  onChange={(e) => setNewChartOfAccount({ ...newChartOfAccount, accountSubType: e.target.value })}
                  disabled={addChartOfAccountLoading}
                >
                  <MenuItem value="">Select Account Sub Type</MenuItem>
                  <MenuItem value="BANK">BANK</MenuItem>
                  <MenuItem value="AR">AR</MenuItem>
                  <MenuItem value="AP">AP</MenuItem>
                  <MenuItem value="FIXED_ASSET">FIXED_ASSET</MenuItem>
                  <MenuItem value="OTHER">OTHER</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Group Code"
                fullWidth
                size='small'
                value={newChartOfAccount.groupCode}
                onChange={(e) => setNewChartOfAccount({ ...newChartOfAccount, groupCode: e.target.value })}
                disabled={addChartOfAccountLoading}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newChartOfAccount.status}
                  size='small'
                  label="Status"
                  onChange={(e) => setNewChartOfAccount({ ...newChartOfAccount, status: e.target.value })}
                  disabled={addChartOfAccountLoading}
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Cash Account Flag</InputLabel>
              <Select
                value={newChartOfAccount.cashAccountFlag}
                size='small'
                label="Cash Account Flag"
                onChange={(e) => setNewChartOfAccount({ ...newChartOfAccount, cashAccountFlag: e.target.value })}
                disabled={addChartOfAccountLoading}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
            {addChartOfAccountError && (
              <Alert severity="error">
                {addChartOfAccountError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => !addChartOfAccountLoading && setAddChartOfAccountDialogOpen(false)}
            disabled={addChartOfAccountLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddChartOfAccount}
            variant="contained"
            disabled={addChartOfAccountLoading}
            sx={harmonateProceedButtonSx}
          >
            {addChartOfAccountLoading ? 'Adding...' : 'Add Chart of Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Chart of Account Dialog */}
      <Dialog
        open={editChartOfAccountDialogOpen}
        onClose={() => !editChartOfAccountLoading && setEditChartOfAccountDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Edit Chart of Account
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          {editingChartOfAccount && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  fullWidth
                  options={propertyApis}
                  getOptionLabel={(option) => {
                    const code = option.propertyCode || '';
                    const name = option.propertyName || '';
                    return code && name ? `${code} - ${name}` : name;
                  }}
                  value={propertyApis.find(p => p.propertyId === editingChartOfAccount.propertyId) || null}
                  onChange={(event, newValue) => {
                    setEditingChartOfAccount({ ...editingChartOfAccount, propertyId: newValue ? newValue.propertyId : '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Property"
                      required
                      placeholder="Select Property"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {editChartOfAccountLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                  sx={{ fontSize: '14px' }}
                />
                <TextField
                  label="GL Code"
                  fullWidth
                  required
                  size='small'
                  value={editingChartOfAccount.glAccountCode}
                  onChange={(e) => {
                    const newCode = e.target.value;
                    setEditingChartOfAccount({
                      ...editingChartOfAccount,
                      glAccountCode: newCode,
                      glAccount: newCode && editingChartOfAccount.glAccountName ? `${newCode} - ${editingChartOfAccount.glAccountName}` : ''
                    });
                  }}
                  disabled={editChartOfAccountLoading}
                />
              </Box>
              <TextField
                label="GL Description"
                fullWidth
                required
                size='small'
                value={editingChartOfAccount.glAccountName}
                onChange={(e) => {
                  const newDescription = e.target.value;
                  setEditingChartOfAccount({
                    ...editingChartOfAccount,
                    glAccountName: newDescription,
                    glAccount: editingChartOfAccount.glAccountCode && newDescription ? `${editingChartOfAccount.glAccountCode} - ${newDescription}` : ''
                  });
                }}
                disabled={editChartOfAccountLoading}
              />
              <TextField
                label="GL Account"
                fullWidth
                size='small'
                value={editingChartOfAccount.glAccount || ''}
                disabled={true}
              // helperText="Auto-populated from GL Code and GL Description"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={editingChartOfAccount.accountType || 'ASSET'}
                    size='small'
                    label="Account Type"
                    onChange={(e) => setEditingChartOfAccount({ ...editingChartOfAccount, accountType: e.target.value })}
                    disabled={editChartOfAccountLoading}
                  >
                    <MenuItem value="ASSET">ASSET</MenuItem>
                    <MenuItem value="LIABILITY">LIABILITY</MenuItem>
                    <MenuItem value="EQUITY">EQUITY</MenuItem>
                    <MenuItem value="INCOME">INCOME</MenuItem>
                    <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Account Sub Type</InputLabel>
                  <Select
                    value={editingChartOfAccount.accountSubType || ''}
                    size='small'
                    label="Account Sub Type"
                    onChange={(e) => setEditingChartOfAccount({ ...editingChartOfAccount, accountSubType: e.target.value })}
                    disabled={editChartOfAccountLoading}
                  >
                    <MenuItem value="">Select Account Sub Type</MenuItem>
                    <MenuItem value="BANK">BANK</MenuItem>
                    <MenuItem value="AR">AR</MenuItem>
                    <MenuItem value="AP">AP</MenuItem>
                    <MenuItem value="FIXED_ASSET">FIXED_ASSET</MenuItem>
                    <MenuItem value="OTHER">OTHER</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Group Code"
                  fullWidth
                  size='small'
                  value={editingChartOfAccount.groupCode}
                  onChange={(e) => setEditingChartOfAccount({ ...editingChartOfAccount, groupCode: e.target.value })}
                  disabled={editChartOfAccountLoading}
                />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editingChartOfAccount.status || 'ACTIVE'}
                    label="Status"
                    size='small'
                    onChange={(e) => setEditingChartOfAccount({ ...editingChartOfAccount, status: e.target.value })}
                    disabled={editChartOfAccountLoading}
                  >
                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                    <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <FormControl fullWidth>
                <InputLabel>Cash Account Flag</InputLabel>
                <Select
                  value={editingChartOfAccount.cashAccountFlag || false}
                  label="Cash Account Flag"
                  size='small'
                  onChange={(e) => setEditingChartOfAccount({ ...editingChartOfAccount, cashAccountFlag: e.target.value })}
                  disabled={editChartOfAccountLoading}
                >
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
              {editChartOfAccountError && (
                <Alert severity="error">
                  {editChartOfAccountError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => !editChartOfAccountLoading && setEditChartOfAccountDialogOpen(false)}
            disabled={editChartOfAccountLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditChartOfAccount}
            variant="contained"
            disabled={editChartOfAccountLoading}
            sx={harmonateProceedButtonSx}
          >
            {editChartOfAccountLoading ? 'Updating...' : 'Update Chart of Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Chart of Account Dialog */}
      <Dialog open={deleteChartOfAccountDialogOpen} onClose={() => setDeleteChartOfAccountDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete chart of account "{deletingChartOfAccount?.glAccountName}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteChartOfAccountDialogOpen(false)}
            disabled={deleteChartOfAccountLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteChartOfAccount}
            variant="contained"
            disabled={deleteChartOfAccountLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteChartOfAccountLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={csvUploadDialogOpen}
        onClose={() => setCsvUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Upload Chart of Accounts</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <DialogContentText sx={{ mb: 2, fontSize: '14px' }}>
            Select a Property and upload your Chart of Accounts data file.
          </DialogContentText>

          {/* Property Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Property
            </Typography>
            {loadingPropertyApis ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Loading Properties...
                </Typography>
              </Box>
            ) : (
              <Autocomplete
                fullWidth
                size="small"
                options={propertyApis}
                getOptionLabel={(option) => {
                  const code = option.propertyCode || '';
                  const name = option.propertyName || '';
                  return code && name ? `${code} - ${name}` : name;
                }}
                value={propertyApis.find(p => p.propertyId === selectedPropertyApi) || null}
                onChange={(event, newValue) => {
                  setSelectedPropertyApi(newValue ? newValue.propertyId : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Property"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPropertyApis ? (
                            <CircularProgress size={20} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      )
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.propertyId === value?.propertyId}
                sx={{ fontSize: '14px' }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      {selectedPropertyApi === option.propertyId && (
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ht.green }} />
                      )}
                      <Typography sx={{ fontSize: '14px' }}>
                        {(() => {
                          const code = option.propertyCode || '';
                          const name = option.propertyName || '';
                          return code && name ? `${code} - ${name}` : name;
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}
          </FormControl>

          {/* Required Headers Information */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: ht.listBg, borderRadius: '4px', border: `1px solid ${ht.line}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#495057' }}>
              Required Headers in your file:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 0, '& li': { fontSize: '13px', color: '#495057', lineHeight: 1.7 } }}>
              <li><strong>GL_CODE</strong> - The GL code (mandatory)</li>
              <li><strong>GL_DESCRIPTION</strong> - The GL description (mandatory)</li>
              <li><strong>GL_ACCOUNT</strong> - The GL account (mandatory)</li>
              <li><strong>ACCOUNT_TYPE</strong> - The account type (optional)</li>
              <li><strong>ACCOUNT_SUB_TYPE</strong> - The account sub type (optional)</li>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6c757d', fontSize: '12px' }}>
              Note: The file must be in CSV or Excel format with these exact header names in the first row.
            </Typography>
          </Box>

          {/* File Upload Area */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Upload File
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000', mb: 2 }}>
              Add your Chart of Accounts file here
            </Typography>

            <HiddenInput
              accept=".csv,.xls,.xlsx"
              id="upload-file"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0] || null;
                if (file) {
                  setUploadFile(file);
                  setUploadError(null);
                }
              }}
            />
            <label htmlFor="upload-file">
              <UploadArea
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  padding: '24px 16px',
                  border: isDragging ? `2px dashed ${ht.green}` : `2px dashed ${ht.line}`,
                  borderRadius: '6px',
                  backgroundColor: isDragging ? ht.secondaryTab : ht.listBg,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: ht.green,
                    backgroundColor: ht.secondaryTab,
                  }
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 32, color: ht.green, mb: 1 }} />
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}>
                  {isDragging ? 'Drop your file here' : 'Drop your file or browse'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#000000', fontSize: '11px' }}>
                  Max 10 MB files are allowed
                </Typography>
              </UploadArea>
            </label>

            {uploadFile && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <InsertDriveFileIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 500,
                      color: '#000000',
                      fontSize: '14px',
                      mb: 0.5
                    }}>
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#000000', fontSize: '12px' }}>
                      {(uploadFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setUploadFile(null);
                    setUploadProgress(0);
                  }}
                  sx={{
                    color: '#000000',
                    '&:hover': { color: '#d32f2f', backgroundColor: '#ffebee' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            )}

            <Typography variant="caption" sx={{ color: '#000000', fontSize: '10px', display: 'block', mt: 1.5 }}>
              Only support CSV and XLS/XLSX
            </Typography>
          </Box>

          {/* Template Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
              Template
            </Typography>
            {loadingTemplates ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Loading Templates...
                </Typography>
              </Box>
            ) : (
              <Select
                value={selectedTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: '14px',
                  '& .MuiOutlinedInput-root': { borderRadius: '4px' }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '14px', color: '#000000', fontStyle: 'italic' }}>
                  No template selected
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id} sx={{ fontSize: '14px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {selectedTemplate === template.id && (
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: ht.green }} />
                      )}
                      <Typography sx={{ fontSize: '14px' }}>
                        {template.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>

          {/* Preview Button */}
          {selectedTemplate && uploadFile && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePreviewClick}
                disabled={!selectedTemplate || !uploadFile}
                sx={harmonateUploadOutlinedSx}
              >
                Preview & Map Columns
              </Button>
            </Box>
          )}

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Uploading... ({uploadProgress}%)
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setCsvUploadDialogOpen(false);
              setUploadFile(null);
              setSelectedPropertyApi('');
              setSelectedTemplate('');
              setCustomMappings({});
              setUploadProgress(0);
              setUploadError(null);
            }}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedPropertyApi && uploadFile) {
                handleCsvUpload();
              } else {
                setUploadError('Please select a Property and upload a file.');
              }
            }}
            variant="contained"
            disabled={!selectedPropertyApi || !uploadFile || uploading}
            sx={harmonateProceedButtonSx}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog for Column Mapping */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          Preview & Map Columns - {previewData.templateName}
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              File: <strong>{previewData.fileName}</strong>
            </Typography>

            {/* Column Mapping Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Excel Column</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Template Field</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Data Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Required</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.excelFields.map((excelField) => {
                    const mappedTemplateField = Object.keys(customMappings).find(key => key === excelField && customMappings[key]) ||
                      Object.keys(previewData.mappings).find(key => previewData.mappings[key]?.excelField === excelField) || '';
                    const template = templates.find(t => t.id === selectedTemplate);
                    const mapping = template?.mappings?.[customMappings[excelField] || mappedTemplateField];

                    return (
                      <TableRow key={excelField}>
                        <TableCell>{excelField}</TableCell>
                        <TableCell>
                          <Select
                            value={customMappings[excelField] || mappedTemplateField}
                            onChange={(e) => handleCustomMapping(excelField, e.target.value)}
                            displayEmpty
                            size="small"
                            fullWidth
                            sx={{ fontSize: '13px' }}
                          >
                            <MenuItem value="">
                              <em>Do not map</em>
                            </MenuItem>
                            {getAvailableTemplateFields(excelField, customMappings[excelField] || mappedTemplateField).map((field) => {
                              const fieldMapping = template?.mappings?.[field];
                              return (
                                <MenuItem key={field} value={field} sx={{ fontSize: '13px' }}>
                                  {field} {fieldMapping?.isRequired && ' *'}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {mapping ? (
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {mapping.dataType || 'TEXT'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping?.isRequired ? (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#d32f2f', fontWeight: 600 }}>
                              Yes
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#000000' }}>
                              No
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Preview Data */}
          {previewData.excelData.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Preview Data (First 5 rows)
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {previewData.templateFields.map((field) => (
                        <TableCell key={field} sx={{ fontWeight: 600, fontSize: '12px' }}>
                          {field} {templates.find(t => t.id === selectedTemplate)?.mappings?.[field]?.isRequired && '*'}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.excelData.slice(0, 5).map((row, index) => (
                      <TableRow key={index}>
                        {previewData.templateFields.map((field) => (
                          <TableCell key={field} sx={{ fontSize: '12px' }}>
                            {row[field] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setPreviewOpen(false)}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePreviewApply}
            variant="contained"
            sx={harmonateProceedButtonSx}
          >
            Apply Mappings
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default ChartOfAccountsDrawer;